import { icons } from '../assets/icons';
import { ReleaseInfo, ReleaseAsset, FALLBACK_RELEASE, FALLBACK_PRERELEASE } from '../config/project-info';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

export class ReleaseModalComponent {
  private stableRelease: ReleaseInfo = FALLBACK_RELEASE;
  private prerelease: ReleaseInfo | null = FALLBACK_PRERELEASE;
  private activeTab: 'stable' | 'prerelease' = 'stable';
  private isOpen: boolean = false;

  constructor(stable: ReleaseInfo, prerelease: ReleaseInfo | null = null) {
    this.stableRelease = stable;
    this.prerelease = prerelease;
  }

  public setReleases(stable: ReleaseInfo, prerelease: ReleaseInfo | null) {
    this.stableRelease = stable;
    this.prerelease = prerelease;
    if (this.isOpen) {
      this.renderContent();
    }
  }

  public open(targetRelease?: ReleaseInfo) {
    if (targetRelease) {
      if (targetRelease.isPrerelease) {
        this.prerelease = targetRelease;
        this.activeTab = 'prerelease';
      } else {
        this.stableRelease = targetRelease;
        this.activeTab = 'stable';
      }
    }

    this.isOpen = true;
    const modalEl = document.getElementById('release-modal-overlay');
    if (modalEl) {
      modalEl.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
      this.renderContent();
    }
  }

  public close() {
    this.isOpen = false;
    const modalEl = document.getElementById('release-modal-overlay');
    if (modalEl) {
      modalEl.classList.add('hidden');
      document.body.style.overflow = '';
    }
  }

  public render(): string {
    return `
      <div 
        id="release-modal-overlay" 
        class="fixed inset-0 z-50 hidden bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      >
        <div 
          id="release-modal-card"
          class="relative w-full max-w-3xl rounded-2xl bg-terminal-bgDark border border-terminal-border shadow-glow-lg overflow-hidden my-8 font-mono"
        >
          <div id="release-modal-inner">
            ${this.getModalInnerHtml()}
          </div>
        </div>
      </div>
    `;
  }

  private getCurrentRelease(): ReleaseInfo {
    if (this.activeTab === 'prerelease' && this.prerelease) {
      return this.prerelease;
    }
    return this.stableRelease;
  }

  private getModalInnerHtml(): string {
    const current = this.getCurrentRelease();
    const rawBody = current.body || 'No release notes provided.';
    const parsedBody = DOMPurify.sanitize(marked.parse(rawBody) as string);
    const isPre = current.isPrerelease;

    return `
      <!-- Modal Header -->
      <div class="p-5 sm:p-6 border-b border-terminal-border bg-terminal-card space-y-4">
        
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="px-2.5 py-0.5 rounded-lg text-xs font-bold ${
              isPre ? 'bg-amber-500 text-black' : 'bg-matrix-500 text-terminal-bgDark shadow-glow-sm'
            }">
              ${current.version}
            </span>

            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
              isPre 
                ? 'bg-amber-950/80 text-amber-300 border border-amber-800' 
                : 'bg-matrix-950/80 text-matrix-400 border border-matrix-800'
            }">
              ${isPre ? icons.flask('w-3 h-3') : icons.badgeCheck('w-3 h-3')}
              <span>${isPre ? 'Pre-Release' : 'Latest Stable'}</span>
            </span>

            <span class="text-xs text-terminal-muted">
              Published on ${current.publishedAt || 'GitHub'}
            </span>
          </div>

          <button 
            id="release-modal-close-btn"
            class="p-2 rounded-lg text-terminal-muted hover:text-terminal-fg hover:bg-terminal-cardLight transition-colors cursor-pointer"
            aria-label="Close modal"
          >
            ${icons.close('w-5 h-5')}
          </button>
        </div>

        <!-- Release Switcher Tabs (If pre-release available) -->
        ${this.prerelease ? `
          <div class="flex items-center gap-2 p-1 rounded-xl bg-terminal-bgDark max-w-sm border border-terminal-border">
            <button 
              id="modal-tab-stable"
              class="flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                this.activeTab === 'stable' 
                  ? 'bg-matrix-500 text-terminal-bgDark shadow-glow' 
                  : 'text-terminal-muted hover:text-terminal-fg'
              }"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-matrix-300"></span>
              <span>Stable (${this.stableRelease.version})</span>
            </button>

            <button 
              id="modal-tab-prerelease"
              class="flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                this.activeTab === 'prerelease' 
                  ? 'bg-amber-500 text-black' 
                  : 'text-terminal-muted hover:text-terminal-fg'
              }"
            >
              ${icons.flask('w-3 h-3 text-amber-400')}
              <span>Pre-release (${this.prerelease.version})</span>
            </button>
          </div>
        ` : ''}

      </div>

      <!-- Modal Body -->
      <div class="p-5 sm:p-6 max-h-[60vh] overflow-y-auto space-y-6 text-left">
        
        <!-- Release Notes Section -->
        <div>
          <h4 class="text-xs font-bold uppercase tracking-wider text-matrix-400 mb-3 flex items-center gap-1.5">
            ${icons.terminal('w-3.5 h-3.5')}
            <span>Changelog & Highlights</span>
          </h4>
          <div class="prose-custom text-xs sm:text-sm">
            ${parsedBody}
          </div>
        </div>

        <!-- Download Packages & Assets -->
        <div class="space-y-3 pt-4 border-t border-terminal-border">
          <h4 class="text-xs font-bold uppercase tracking-wider text-cyber-400 flex items-center gap-1.5">
            ${icons.download('w-3.5 h-3.5')}
            <span>Native Packages & Binaries (${current.assets.length})</span>
          </h4>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            ${current.assets.map(asset => `
              <a 
                href="${asset.downloadUrl}"
                target="_blank"
                class="p-3 rounded-xl border border-terminal-border hover:border-matrix-500/60 bg-terminal-card hover:bg-terminal-cardLight flex items-center justify-between group transition-all"
              >
                <div class="flex items-center gap-2.5 overflow-hidden">
                  <span class="text-matrix-400 group-hover:text-matrix-300">${this.getAssetIcon(asset.type)}</span>
                  <div class="text-xs truncate">
                    <div class="font-semibold text-terminal-fg group-hover:text-matrix-400 truncate">
                      ${asset.name}
                    </div>
                    <div class="text-terminal-muted text-[11px]">${asset.size}</div>
                  </div>
                </div>
                ${icons.download('w-4 h-4 text-terminal-muted group-hover:text-matrix-400 transition-colors flex-shrink-0')}
              </a>
            `).join('')}
          </div>
        </div>

      </div>

      <!-- Modal Footer -->
      <div class="p-4 border-t border-terminal-border bg-terminal-card flex items-center justify-between">
        <a 
          href="${current.htmlUrl}"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs font-semibold text-matrix-400 hover:text-matrix-300 hover:underline flex items-center gap-1"
        >
          <span>View ${current.version} on GitHub</span>
          ${icons.externalLink('w-3.5 h-3.5')}
        </a>

        <button 
          id="release-modal-done-btn"
          class="px-4 py-2 rounded-xl text-xs font-bold bg-matrix-500 text-terminal-bgDark hover:bg-matrix-400 transition-colors cursor-pointer"
        >
          Close
        </button>
      </div>
    `;
  }

  private getAssetIcon(type: ReleaseAsset['type']): string {
    switch (type) {
      case 'deb':
      case 'rpm':
      case 'arch':
        return icons.server('w-4 h-4');
      case 'appimage':
      case 'binary':
      case 'windows':
        return icons.terminal('w-4 h-4');
      case 'whl':
      case 'tar':
        return icons.packageIcon('w-4 h-4');
      case 'checksum':
        return icons.shield('w-4 h-4');
      default:
        return icons.packageIcon('w-4 h-4');
    }
  }

  private renderContent() {
    const innerEl = document.getElementById('release-modal-inner');
    if (innerEl) {
      innerEl.innerHTML = this.getModalInnerHtml();
      this.bindEvents();
    }
  }

  public bindEvents() {
    const overlay = document.getElementById('release-modal-overlay');
    const closeBtn = document.getElementById('release-modal-close-btn');
    const doneBtn = document.getElementById('release-modal-done-btn');

    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    if (doneBtn) doneBtn.addEventListener('click', () => this.close());

    const stableTab = document.getElementById('modal-tab-stable');
    if (stableTab) {
      stableTab.addEventListener('click', () => {
        this.activeTab = 'stable';
        this.renderContent();
      });
    }

    const preTab = document.getElementById('modal-tab-prerelease');
    if (preTab) {
      preTab.addEventListener('click', () => {
        this.activeTab = 'prerelease';
        this.renderContent();
      });
    }

    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.close();
        }
      });
    }

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }
}
